import React from 'react'
import * as ReactDOM from 'react-dom';
import {select, put, call, takeEvery, takeLeading} from 'redux-saga/effects';
import moment from 'moment';
import camelize from 'camelize';

import history from '../../services/history';
import * as actionTypes from '../actions/reservations/actionTypes';
import * as actions from '../actions/';

//Utils
import qs from 'qs';
import axios from '../../services/axios-client';
import i18n from "../../translations/i18n";

import Notification from '../../components/Notifications/Notifications';

import { PAYMENT_TYPE } from '../../const/payment/PAYMENT_TYPE';

export function* getReservations({start = 0, count = 10, loadMore = false}) {
    try {
        const noMoreBookings = yield select(state => state.reservations.noMoreBookings);
        const bundlesList = yield select(state => state.reservations.bundles);
        if(noMoreBookings && loadMore) {
            yield put(actions.getReservationsSucceeded({bookings: [], bundles: bundlesList}, loadMore));
            return;
        }

        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/reservation/list',
            data: qs.stringify({
                next: 1,
                first_result: start,
                max_results: count
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });
        const {bookings, bundles} = resp.data.result;
        yield put(actions.getReservationsSucceeded({bookings, bundles}, loadMore));
    } catch (err) {
        if(err.response && err.response.status === 401) {
            yield put(actions.logout());
        }
        yield put(actions.getReservationsFailed(err));
    }
}

export function* getPastReservations({start = 0, count = 10, loadMore = false}) {
    try {
        const bundlesList = yield select(state => state.reservations.pastBundles);
        const noMorePastBookings = yield select(state => state.reservations.noMorePastBookings);
        if(noMorePastBookings && loadMore) {
            yield put(actions.getPastReservationsSucceeded({bookings: [], bundles: bundlesList}, loadMore));
            return;
        }

        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/reservation/list',
            data: qs.stringify({
                next: 0,
                first_result: start,
                max_results: count
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });
        const {bookings, bundles} = resp.data.result;
        yield put(actions.getPastReservationsSucceeded({bookings, bundles}, loadMore));
    } catch (err) {
        yield put(actions.getPastReservationsFailed(err))
    }
}

export function* getReservationDetails({options: {id, type, next}}) {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/reservation/details',
            data: qs.stringify({
                reservation_id: id,
                reservation_type: type,
                next: Number(next).toString()
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });

        const [details] = resp.data.result;
        yield put(actions.getReservationDetailsSucceeded(details));
    } catch(err) {
        yield put(actions.getReservationDetailsFailed(err));
        ReactDOM.render(
            <Notification type='error' error={err}/>,
            document.getElementById('notification')
        );
    }
}

export function* cancelBooking({id}) {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        yield axios.request({
            method: 'POST',
            url: '/api/client/booking/cancel',
            data: qs.stringify({
                booking_id: id
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });

        const reservation = yield select(state => state.reservations.reservationDetails)
        const {
            finalCost,
            timestamp,
            cancellationPolicy,
            paid,
            paymentInfo,
        } = camelize(reservation)
        const isWithRefund = Boolean(finalCost) && paid && paymentInfo.toLowerCase() === PAYMENT_TYPE.IDEAL
            && moment.unix(timestamp).diff(moment(), 'hours') > (cancellationPolicy ?? 0)

        yield call(history.push, '/reservations');
        ReactDOM.render(
            <Notification 
                type='success' 
                message={i18n.t(
                    isWithRefund
                        ? 'common:reservationScreen.bookingCancelledWithRefund'
                        : 'common:reservationScreen.bookingCancelled'
                )}
            />,
            document.getElementById('notification')
        );
        yield put(actions.cancelBookingSucceeded(id));
        yield put(actions.getProfile());
    } catch(err) {
        yield call(history.push, '/reservations');

        ReactDOM.render(
            <Notification type='error' error={err}/>,
            document.getElementById('notification')
        );
    }
}

export const watchReservations = [
    takeEvery(actionTypes.GET_RESERVATIONS, getReservations),
    takeEvery(actionTypes.GET_PAST_RESERVATIONS, getPastReservations),
    takeEvery(actionTypes.GET_RESERVATION_DETAILS, getReservationDetails),
    takeLeading(actionTypes.CANCEL_BOOKING, cancelBooking)
];
